import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, CalendarIcon, DollarSignIcon, PercentIcon, InfoIcon } from 'lucide-react';
import { Campaign } from '../types/Campaign';
// Mock campaigns data - would be replaced with API calls in a real app
const mockCampaigns: Record<string, Campaign> = {
  RBLSHOP2025: {
    id: 'RBLSHOP2025',
    title: '[RLife] 7-days Promotion for Shop',
    description: '[RLife] 7-days Promotion for Shop',
    expiryDate: '07/10/2025',
    discountPercentage: 0,
    fixedDiscountAmount: 50,
    currency: 'THB',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 5000,
    budgetUsed: 0,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'RBLSHOP2025',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 50,
    maxDiscount: 50,
    status: 'active',
    companies: ['Rabbit Life']
  },
  BBL10THB: {
    id: 'BBL10THB',
    title: 'BBL Special Package (10 THB Discount)',
    description: 'DISCOUNT_COUPON',
    expiryDate: '02/08/2025',
    discountPercentage: 0,
    fixedDiscountAmount: 10,
    currency: 'THB',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 11000,
    budgetUsed: 10960,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'BBL10THB',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 10,
    maxDiscount: 10,
    status: 'active'
  }
};
// Empty campaign template for new campaigns
const emptyCampaign: Campaign = {
  id: '',
  title: '',
  description: '',
  expiryDate: '',
  discountPercentage: 0,
  fixedDiscountAmount: 0,
  currency: 'THB',
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budget: 0,
  budgetUsed: 0,
  strictlyEnforceBudget: true,
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: '',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Global',
  minTransactionValue: 0,
  maxTransactionValue: 999999,
  minDiscount: 0,
  maxDiscount: 0,
  status: 'active'
};
export function CampaignForm() {
  const [campaign, setCampaign] = useState<Campaign>(emptyCampaign);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState<Record<string, boolean>>({
    Sunday: true,
    Monday: true,
    Tuesday: true,
    Wednesday: true,
    Thursday: true,
    Friday: true,
    Saturday: true
  });
  useEffect(() => {
    // In a real app, we would fetch the campaign data from an API
    // For now, we'll use the URL parameter to get the campaign ID
    const params = new URLSearchParams(window.location.search);
    const campaignId = params.get('id');
    if (campaignId && mockCampaigns[campaignId]) {
      setCampaign(mockCampaigns[campaignId]);
      setIsEditing(true);
      // Set selected days based on campaign data
      const days = mockCampaigns[campaignId].validDays.reduce((acc, day) => {
        acc[day] = true;
        return acc;
      }, {} as Record<string, boolean>);
      setSelectedDays(days);
    }
    setLoading(false);
  }, []);
  const navigateBack = () => {
    window.location.href = isEditing ? `/campaign-details?id=${campaign.id}` : '/campaigns';
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    // Handle checkbox inputs
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setCampaign({
        ...campaign,
        [name]: checkbox.checked
      });
      return;
    }
    // Handle numeric inputs
    if (type === 'number') {
      setCampaign({
        ...campaign,
        [name]: parseFloat(value) || 0
      });
      return;
    }
    // Handle all other inputs
    setCampaign({
      ...campaign,
      [name]: value
    });
  };
  const handleDayToggle = (day: string) => {
    const newSelectedDays = {
      ...selectedDays,
      [day]: !selectedDays[day]
    };
    setSelectedDays(newSelectedDays);
    // Update campaign with selected days
    const validDays = Object.entries(newSelectedDays).filter(([_, isSelected]) => isSelected).map(([day]) => day);
    setCampaign({
      ...campaign,
      validDays
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, we would send the campaign data to an API
    // For now, we'll just log it and navigate back
    console.log('Submitting campaign:', campaign);
    // Generate an ID for new campaigns
    if (!isEditing) {
      const newId = campaign.title.replace(/\s+/g, '').toUpperCase().substring(0, 10);
      setCampaign({
        ...campaign,
        id: newId
      });
    }
    // Navigate to the campaign details page
    window.location.href = '/campaigns';
  };
  if (loading) {
    return <div className="flex justify-center items-center h-64" data-id="element-2716">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" data-id="element-2717"></div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-2718">
      {/* Top bar with actions */}
      <div className="flex justify-between items-center" data-id="element-2719">
        <button onClick={navigateBack} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-2720">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-2721" />
          <span data-id="element-2722">Back</span>
        </button>
        <h1 className="text-2xl font-bold text-gray-800" data-id="element-2723">
          {isEditing ? 'Update Campaign' : 'Create Campaign'}
        </h1>
        <div className="w-24" data-id="element-2724"></div> {/* Empty div for spacing */}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2725">
        <form onSubmit={handleSubmit} data-id="element-2726">
          <div className="space-y-6" data-id="element-2727">
            <div data-id="element-2728">
              <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-2729">
                Campaigns
              </h2>
              {/* Title */}
              <div className="mb-4" data-id="element-2730">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2731">
                  Title <span className="text-red-500" data-id="element-2732">*</span>
                </label>
                <input type="text" id="title" name="title" value={campaign.title} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-2733" />
              </div>
              {/* Description */}
              <div className="mb-4" data-id="element-2734">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2735">
                  Description
                </label>
                <textarea id="description" name="description" value={campaign.description} onChange={handleInputChange} rows={3} className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-2736" />
              </div>
              {/* Expiry Date */}
              <div className="mb-4" data-id="element-2737">
                <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2738">
                  Expiry Date <span className="text-red-500" data-id="element-2739">*</span>
                </label>
                <div className="relative" data-id="element-2740">
                  <input type="text" id="expiryDate" name="expiryDate" placeholder="DD/MM/YYYY" value={campaign.expiryDate} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 pr-10" data-id="element-2741" />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-2742">
                    <CalendarIcon size={18} className="text-gray-400" data-id="element-2743" />
                  </div>
                </div>
              </div>
              {/* Discount Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-2744">
                <div data-id="element-2745">
                  <label htmlFor="discountPercentage" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2746">
                    Discount Percentage
                  </label>
                  <div className="relative" data-id="element-2747">
                    <input type="number" id="discountPercentage" name="discountPercentage" value={campaign.discountPercentage} onChange={handleInputChange} min="0" max="100" className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 pr-10" data-id="element-2748" />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-2749">
                      <PercentIcon size={18} className="text-gray-400" data-id="element-2750" />
                    </div>
                  </div>
                </div>
                <div data-id="element-2751">
                  <label htmlFor="fixedDiscountAmount" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2752">
                    Fixed discount amount
                  </label>
                  <div className="flex" data-id="element-2753">
                    <select id="currency" name="currency" value={campaign.currency} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-l-md focus:ring-blue-500 focus:border-blue-500" data-id="element-2754">
                      <option value="THB" data-id="element-2755">THB</option>
                      <option value="USD" data-id="element-2756">USD</option>
                      <option value="EUR" data-id="element-2757">EUR</option>
                    </select>
                    <input type="number" id="fixedDiscountAmount" name="fixedDiscountAmount" value={campaign.fixedDiscountAmount} onChange={handleInputChange} min="0" className="flex-1 p-2 border-y border-r border-gray-300 rounded-r-md focus:ring-blue-500 focus:border-blue-500" data-id="element-2758" />
                  </div>
                </div>
              </div>
              {/* Budget */}
              <div className="mb-4" data-id="element-2759">
                <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2760">
                  Budget
                </label>
                <div className="flex" data-id="element-2761">
                  <select id="budgetCurrency" name="currency" value={campaign.currency} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-l-md focus:ring-blue-500 focus:border-blue-500" disabled data-id="element-2762">
                    <option value="THB" data-id="element-2763">THB</option>
                  </select>
                  <input type="number" id="budget" name="budget" value={campaign.budget} onChange={handleInputChange} min="0" className="flex-1 p-2 border-y border-r border-gray-300 rounded-r-md focus:ring-blue-500 focus:border-blue-500" data-id="element-2764" />
                </div>
              </div>
              {/* Budget Used */}
              <div className="mb-4" data-id="element-2765">
                <label htmlFor="budgetUsed" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2766">
                  Budget already used
                </label>
                <div className="flex" data-id="element-2767">
                  <select id="budgetUsedCurrency" name="currency" value={campaign.currency} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-l-md focus:ring-blue-500 focus:border-blue-500" disabled data-id="element-2768">
                    <option value="THB" data-id="element-2769">THB</option>
                  </select>
                  <input type="number" id="budgetUsed" name="budgetUsed" value={campaign.budgetUsed} onChange={handleInputChange} min="0" className="flex-1 p-2 border-y border-r border-gray-300 rounded-r-md focus:ring-blue-500 focus:border-blue-500" disabled={!isEditing} data-id="element-2770" />
                </div>
              </div>
              {/* Recurring Budget */}
              <div className="mb-4" data-id="element-2771">
                <p className="text-sm text-gray-700 mb-1" data-id="element-2772">
                  Optionally specify a recurring budget (the budget will reset
                  automatically for each period until expiry)
                </p>
                <select id="recurringBudgetPeriod" name="recurringBudgetPeriod" value={campaign.recurringBudgetPeriod || ''} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 mb-2" data-id="element-2773">
                  <option value="" data-id="element-2774">-- Select One --</option>
                  <option value="daily" data-id="element-2775">Daily</option>
                  <option value="weekly" data-id="element-2776">Weekly</option>
                  <option value="monthly" data-id="element-2777">Monthly</option>
                </select>
                <p className="text-sm text-gray-700 mb-1" data-id="element-2778">
                  Recurring budget for each period (if you have specified a
                  total campaign budget this will still apply)
                </p>
                <div className="flex" data-id="element-2779">
                  <select id="recurringBudgetCurrency" name="currency" value={campaign.currency} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-l-md focus:ring-blue-500 focus:border-blue-500" disabled data-id="element-2780">
                    <option value="THB" data-id="element-2781">THB</option>
                  </select>
                  <input type="number" id="recurringBudget" name="recurringBudget" value={campaign.recurringBudget || ''} onChange={handleInputChange} min="0" className="flex-1 p-2 border-y border-r border-gray-300 rounded-r-md focus:ring-blue-500 focus:border-blue-500" data-id="element-2782" />
                </div>
              </div>
              {/* Strictly Enforce Budget */}
              <div className="mb-4 flex items-center" data-id="element-2783">
                <input type="checkbox" id="strictlyEnforceBudget" name="strictlyEnforceBudget" checked={campaign.strictlyEnforceBudget} onChange={e => setCampaign({
                ...campaign,
                strictlyEnforceBudget: e.target.checked
              })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2784" />
                <label htmlFor="strictlyEnforceBudget" className="ml-2 block text-sm text-gray-700" data-id="element-2785">
                  Strictly enforce budget
                </label>
              </div>
              {/* Payment Providers */}
              <div className="mb-4" data-id="element-2786">
                <label htmlFor="paymentProviders" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2787">
                  Payment providers details
                </label>
                <select id="paymentProviders" name="paymentProviders" className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-2788">
                  <option value="" data-id="element-2789">-- Select One --</option>
                  <option value="all" data-id="element-2790">All Payment Providers</option>
                </select>
              </div>
              {/* Companies */}
              <div className="mb-4" data-id="element-2791">
                <label htmlFor="companies" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2792">
                  Companies (If no company is selected this campaign will apply
                  to all companies)
                </label>
                <select id="companies" name="companies" className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-2793">
                  <option value="" data-id="element-2794">-- Select One --</option>
                  <option value="rabbit_life" data-id="element-2795">Rabbit Life</option>
                </select>
              </div>
              {/* Limit Usage */}
              <div className="mb-4" data-id="element-2796">
                <label htmlFor="limitUsage" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2797">
                  Limit usage for a specified customer/id (localCustomerId)
                </label>
                <input type="text" id="limitUsage" name="limitUsage" value={campaign.limitUsage || ''} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-2798" />
              </div>
              {/* Valid MCC */}
              <div className="mb-4" data-id="element-2799">
                <label htmlFor="validMCC" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2800">
                  List of valid MCC
                </label>
                <select id="validMCC" name="validMCC" className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-2801">
                  <option value="" data-id="element-2802">-- Select One --</option>
                  <option value="all" data-id="element-2803">All MCCs</option>
                </select>
              </div>
              {/* Valid Days */}
              <div className="mb-4" data-id="element-2804">
                <p className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2805">
                  Valid on which days (nothing selected means valid on all days)
                </p>
                <div className="space-y-2" data-id="element-2806">
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => <div key={day} className="flex items-center" data-id="element-2807">
                      <input type="checkbox" id={`day-${day}`} checked={selectedDays[day] || false} onChange={() => handleDayToggle(day)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2808" />
                      <label htmlFor={`day-${day}`} className="ml-2 block text-sm text-gray-700" data-id="element-2809">
                        {day}
                      </label>
                    </div>)}
                </div>
              </div>
              {/* Code */}
              <div className="mb-4" data-id="element-2810">
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2811">
                  Campaign Code <span className="text-red-500" data-id="element-2812">*</span>
                </label>
                <input type="text" id="code" name="code" value={campaign.code} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-2813" />
              </div>
              {/* Submit Button */}
              <div className="flex justify-end mt-6" data-id="element-2814">
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2815">
                  Submit
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>;
}